import { ApiError } from "@groundspark/browser-auth";
import { formatElectionDate, renderVoteShares, type ElectionCandidate } from "./elections";
import type { HoveredMapArea } from "./map-view";

export type CouncilGeography = "council_ward" | "county_division";
type Candidate = Pick<ElectionCandidate, "candidateName" | "partyName" | "voteCount">;

export interface CouncilResult {
  readonly electionId: string;
  readonly electionDate: string;
  readonly geography: CouncilGeography;
  readonly areaCode: string;
  readonly areaName: string;
  readonly authorityName: string;
  readonly electorateCount: number | null;
  readonly validBallotCount: number | null;
  readonly candidates: readonly Candidate[];
  readonly allocationWeight: number;
}

export interface CouncilAreaResults {
  readonly baseAreaCount: number;
  readonly coveredBaseAreaCounts: Readonly<Record<CouncilGeography, number>>;
  readonly results: readonly CouncilResult[];
}

export function parseCouncilAreaResults(value: unknown): CouncilAreaResults {
  const root = record(value);
  const baseAreaCount = count(root.base_area_count);
  const coverage = record(root.covered_base_area_counts);
  const coveredBaseAreaCounts = {
    council_ward: count(coverage.council_ward),
    county_division: count(coverage.county_division),
  };
  if (
    Object.values(coveredBaseAreaCounts).some((covered) => covered > baseAreaCount) ||
    !Array.isArray(root.results)
  )
    throw new ApiError("Invalid council election coverage.");
  const seen = new Set<string>();
  const results = root.results.map((value: unknown): CouncilResult => {
    const contribution = record(value);
    const row = record(contribution.result);
    const electionId = text(row.election_id);
    const electionDate = text(row.election_date);
    const allocationWeight = contribution.allocation_weight;
    const electorateCount = row.electorate_count === null ? null : count(row.electorate_count);
    const validBallotCount =
      row.valid_ballot_count === null ? null : count(row.valid_ballot_count);
    if (
      seen.has(electionId) ||
      !/^\d{4}-\d{2}-\d{2}$/.test(electionDate) ||
      !Number.isFinite(Date.parse(electionDate)) ||
      new Date(electionDate).toISOString().slice(0, 10) !== electionDate ||
      (row.geography !== "council_ward" && row.geography !== "county_division") ||
      typeof allocationWeight !== "number" ||
      !Number.isFinite(allocationWeight) ||
      allocationWeight < 0 ||
      allocationWeight > 1 ||
      !Array.isArray(row.candidates) ||
      row.candidates.length === 0 ||
      electorateCount === 0 ||
      (validBallotCount !== null &&
        electorateCount !== null &&
        validBallotCount > electorateCount)
    )
      throw new ApiError("Invalid council election result.");
    seen.add(electionId);
    const candidates = row.candidates
      .map((value: unknown) => {
        const candidate = record(value);
        return {
          candidateName: text(candidate.candidate_name),
          partyName: text(candidate.party_name),
          voteCount: count(candidate.vote_count),
        };
      })
      .sort(
        (a, b) => b.voteCount - a.voteCount || a.candidateName.localeCompare(b.candidateName),
      );
    const total = candidates.reduce((sum, candidate) => sum + candidate.voteCount, 0);
    if (
      !Number.isSafeInteger(total) ||
      total <= 0 ||
      candidates.some(
        ({ voteCount }) =>
          (electorateCount !== null && voteCount > electorateCount) ||
          (validBallotCount !== null && voteCount > validBallotCount),
      )
    )
      throw new ApiError("Invalid council candidate vote total.");
    return {
      electionId,
      electionDate,
      allocationWeight,
      candidates,
      geography: row.geography,
      areaCode: text(row.area_code),
      areaName: text(row.area_name),
      authorityName: text(row.authority_name),
      electorateCount,
      validBallotCount,
    };
  });
  for (const geography of ["council_ward", "county_division"] as const) {
    if (
      coveredBaseAreaCounts[geography] > 0 !==
      results.some((result) => result.geography === geography)
    )
      throw new ApiError("Council results do not match their coverage.");
  }
  return { baseAreaCount, coveredBaseAreaCounts, results };
}

export function aggregateCouncilVotes(results: readonly CouncilResult[]): readonly Candidate[] {
  const parties = new Map<string, number>();
  for (const result of results) {
    for (const candidate of result.candidates) {
      parties.set(
        candidate.partyName,
        (parties.get(candidate.partyName) ?? 0) + candidate.voteCount * result.allocationWeight,
      );
    }
  }
  return [...parties]
    .map(([partyName, voteCount]) => ({ partyName, voteCount, candidateName: "" }))
    .sort((a, b) => b.voteCount - a.voteCount || a.partyName.localeCompare(b.partyName));
}

export function councilParticipation(results: readonly CouncilResult[], aggregate: boolean) {
  let electorateCount: number | null = 0;
  let noValidVoteCount: number | null = 0;
  for (const result of results) {
    const weight = aggregate ? result.allocationWeight : 1;
    if (weight === 0) continue;
    if (result.electorateCount === null) {
      electorateCount = null;
      noValidVoteCount = null;
      continue;
    }
    if (electorateCount !== null) electorateCount += result.electorateCount * weight;
    if (result.validBallotCount === null) noValidVoteCount = null;
    else if (noValidVoteCount !== null)
      noValidVoteCount += (result.electorateCount - result.validBallotCount) * weight;
  }
  return { electorateCount, noValidVoteCount, estimated: aggregate };
}

function renderCouncilVoteShares(
  container: HTMLElement,
  candidates: readonly Candidate[],
  results: readonly CouncilResult[],
  aggregate: boolean,
): void {
  const participation = councilParticipation(results, aggregate);
  appendText(
    container,
    "p",
    participation.electorateCount === null
      ? "Share of candidate votes · electorate unavailable"
      : "Share of electorate · (none) includes nonvoters and rejected ballots",
  );
  if (
    participation.electorateCount !== null &&
    results.some(
      (result) =>
        result.validBallotCount === null ||
        result.candidates.reduce((sum, candidate) => sum + candidate.voteCount, 0) >
          result.validBallotCount,
    )
  )
    appendText(
      container,
      "p",
      "Voters may choose multiple candidates; shares can total more than 100%.",
    );
  const list = document.createElement("ol");
  list.className = "vote-shares";
  list.setAttribute(
    "aria-label",
    participation.electorateCount === null ? "Candidate vote shares" : "Shares of electorate",
  );
  container.append(list);
  renderVoteShares(list, candidates, participation);
}

export function renderCouncilResults(
  container: HTMLElement,
  area: HoveredMapArea,
  data: CouncilAreaResults,
): void {
  container.replaceChildren();
  const aggregate = area.geography === "constituency" || area.geography === "settlement";
  if (data.results.length === 0) {
    appendText(container, "p", `No local results for ${area.name}.`);
    return;
  }
  for (const geography of ["council_ward", "county_division"] as const) {
    const results = data.results
      .filter((result) => result.geography === geography)
      .sort(
        (a, b) =>
          a.authorityName.localeCompare(b.authorityName) ||
          a.areaName.localeCompare(b.areaName) ||
          a.electionDate.localeCompare(b.electionDate),
      );
    const label = geography === "council_ward" ? "Council wards" : "County divisions";
    if (results.length === 0) continue;
    const section = document.createElement("section");
    section.className = "election-group";
    container.append(section);
    appendText(section, "h2", label);
    if (aggregate) {
      appendText(section, "p", area.name, "election-area");
      const dates = results.map((result) => result.electionDate).sort();
      const first = formatElectionDate(dates[0]!);
      const last = formatElectionDate(dates.at(-1)!);
      appendText(
        section,
        "p",
        `Estimated votes · ${results.length} ${results.length === 1 ? "election" : "elections"} · ${first === last ? first : `${first} – ${last}`}`,
      );
      const votes = aggregateCouncilVotes(results);
      renderCouncilVoteShares(section, votes, results, true);
    } else {
      for (const result of results) {
        const contest = document.createElement("div");
        contest.className = "election-contest";
        section.append(contest);
        appendText(contest, "p", result.areaName, "election-area");
        appendText(
          contest,
          "p",
          `${result.authorityName} · ${formatElectionDate(result.electionDate)}`,
        );
        renderCouncilVoteShares(contest, result.candidates, [result], false);
      }
    }
    const covered = data.coveredBaseAreaCounts[geography];
    if (covered < data.baseAreaCount) {
      appendText(
        section,
        "p",
        `Partial coverage: ${covered.toLocaleString("en-GB")}/${data.baseAreaCount.toLocaleString("en-GB")} base areas`,
        "election-context election-coverage",
      );
    }
  }
}

function appendText(
  parent: HTMLElement,
  tag: "h2" | "p",
  value: string,
  className = tag === "p" ? "election-context" : "",
): void {
  const element = document.createElement(tag);
  element.className = className;
  element.textContent = value;
  parent.append(element);
}

function record(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    throw new ApiError("Invalid council election data.");
  return value as Record<string, unknown>;
}
function text(value: unknown): string {
  if (typeof value !== "string" || value.trim().length === 0)
    throw new ApiError("Missing council election text.");
  return value;
}
function count(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0)
    throw new ApiError("Invalid council election count.");
  return value;
}
